import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { switchAccountType } from '@shared/auth/switchAccountType';
import { useAuth } from '@shared/auth/AuthContext';
import { CREATOR_VERIFY_POST_URL, CREATOR_OFFICIAL_HANDLE } from './creatorConfig';
import type { PendingClaim } from './useCreator';

// Creator onboarding. Start state (full screen): enter @handle → the cabinet opens at once
// (read-only until verified). Pending state: prove you own @handle by posting a one-time
// code as a comment under Adamar's official post — shown `embedded` inside the cabinet
// (Profile tab), not as a wall in front of it. Verification itself is done by the
// service-side verifier (comment scrape) / admin — this card just guides + polls status.
export default function CreatorClaim({ pending, onRefetch, embedded }: { pending: PendingClaim | null; onRefetch: () => void; embedded?: boolean }) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const [handle, setHandle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [claim, setClaim] = useState<PendingClaim | null>(pending);
  const [copied, setCopied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);
  const [switchingType, setSwitchingType] = useState(false);
  const { signOut } = useAuth();
  const lastCheckAt = useRef(0);

  const becomeAdvertiser = async () => {
    setSwitchingType(true);
    try { await switchAccountType('advertiser'); }
    catch (e) { setErr((e as Error)?.message ?? String(e)); setSwitchingType(false); }
  };
  const switchRow = (
    <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: 'var(--dim)' }}>
      {tr('creator.claim.notCreator', 'Вы рекламодатель, а не блогер?')}{' '}
      <button
        type="button" onClick={becomeAdvertiser} disabled={switchingType}
        style={{ background: 'none', border: 0, padding: 0, color: '#8ab4ff', cursor: switchingType ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}
      >
        {switchingType ? '…' : tr('creator.claim.switchToAdvertiser', 'Перейти в кабинет рекламодателя')}
      </button>
      <div style={{ marginTop: 10 }}>
        <button type="button" onClick={signOut}
          style={{ background: 'none', border: 0, padding: 0, color: 'var(--dim)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}>
          {tr('creator.claim.signOut', 'Выйти')}
        </button>
      </div>
    </div>
  );

  // Wrong @handle (typo, second account): cancel the pending claim and go back to the
  // handle field with the old value prefilled, instead of being stuck on a code forever.
  const changeHandle = async () => {
    if (!claim?.id) return;
    setErr(null); setBusy(true);
    const { error } = await supabase.rpc('creator_claim_withdraw', { p_claim_id: claim.id });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setHandle(claim.handle ?? '');
    setClaim(null); setCheckMsg(null);
    onRefetch();
  };

  const start = async (h0?: string) => {
    const h = (h0 ?? handle).trim().replace(/^@/, '');
    if (!h) return;
    setErr(null); setBusy(true);
    const { data, error } = await supabase.rpc('creator_claim_start', { p_handle: h });
    setBusy(false);
    if (error) {
      const m = error.message || '';
      setErr(m.includes('invalid_handle') ? tr('creator.claim.errInvalid', 'Проверьте @никнейм — только буквы, цифры, точки и подчёркивания.')
        : m.includes('handle_is_brand') ? tr('creator.claim.errIsBrand', 'Похоже, это бренд, а не блогер. Войдите через кабинет рекламодателя.')
        : m.includes('already_claimed') ? tr('creator.claim.errClaimed', 'Этот аккаунт уже привязан к другому профилю.')
        : m);
      return;
    }
    const d = data as { claim_id: string; code: string; handle: string; instagram_provider_id: string };
    setClaim({ id: d.claim_id, claim_code: d.code, instagram_provider_id: d.instagram_provider_id, status: 'pending', handle: d.handle });
    onRefetch(); // the cabinet opens now; the code waits in Profile until they want to act
  };

  // Auto-start the claim with the handle captured at signup (stashed in localStorage),
  // so a new creator lands straight on their ADAMAR code — no need to re-type the handle.
  useEffect(() => {
    if (claim) return;
    let h = '';
    try { h = localStorage.getItem('adamar_claim_handle') || ''; } catch { /* ignore */ }
    if (!h) return;
    try { localStorage.removeItem('adamar_claim_handle'); } catch { /* ignore */ }
    setHandle(h);
    void start(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (s: string) => {
    navigator.clipboard?.writeText(s).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  // On-demand verification: this button IS the scrape trigger (there is no background
  // cron — we don't blindly scrape the post all day). It calls the backend verifier,
  // which scrapes the shared post's comments, matches username+code and confirms. The
  // scrape is rate-limited per-post server-side, so concurrent clicks share one scrape.
  // If confirmed, the parent (CreatorApp) refetches and unmounts this screen.
  /** Has the server already resolved my claim? Free DB read, no scrape. */
  const alreadyResolved = async (): Promise<boolean> => {
    if (!claim?.id) return false;
    const { data } = await supabase.from('creator_claims').select('status').eq('id', claim.id).maybeSingle();
    return !!data && data.status !== 'pending';
  };

  const checkStatus = async () => {
    setChecking(true);
    let failed: string | null = null;

    // One scrape reads EVERY comment on the shared post, so it confirms every creator
    // waiting — not just the one who pressed. If two people sign up minutes apart, the
    // first press already verified the second, and making them pay for another scrape
    // (and then telling them "we don't see your comment" because there was nothing left
    // to confirm) was the whole bug. Ask the database first; it is free.
    if (await alreadyResolved()) {
      try { sessionStorage.setItem('adamar_just_claimed', '1'); } catch { /* ignore */ } // → post-claim install nudge
      try { await supabase.auth.refreshSession(); } catch { /* non-fatal */ }
      onRefetch(); setChecking(false); setCheckMsg(null);
      return;
    }

    // Give Instagram a moment to surface the just-posted comment to the public feed the
    // scraper reads — propagation lags ~10-30s.
    setCheckMsg(tr('creator.claim.checkingWait', 'Проверяем комментарий — это займёт до трёх минут…'));
    lastCheckAt.current = Date.now();
    await new Promise((r) => setTimeout(r, 18000));
    let confirmedAny = false;
    let throttled = 0;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (token) {
        const r = await fetch('/api/verify-my-claim', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(String(r.status));
        const j = (await r.json()) as { throttled?: boolean; retry_after_sec?: number; confirmed?: { ok?: boolean }[] };
        if (j?.throttled) throttled = Number(j.retry_after_sec) || 15;
        confirmedAny = Array.isArray(j?.confirmed) && j.confirmed.some((c) => c.ok);
      }
      // Trust the database over the response. A scrape that ran while we waited — ours or
      // someone else's — may have confirmed us without this call reporting anything.
      if (!confirmedAny) confirmedAny = await alreadyResolved();
    } catch (e) {
      // A failed request is NOT "your comment isn't there". Telling people the latter
      // when we never managed to look sent them off to fix a comment that was fine.
      failed = String(e);
    }
    // A confirmed claim grants creator features in the DB, but tab/access visibility
    // reads the JWT claim (baked at token issue). Force a refresh so it applies without
    // re-login; AuthContext picks it up via TOKEN_REFRESHED.
    if (confirmedAny) {
      try { sessionStorage.setItem('adamar_just_claimed', '1'); } catch { /* ignore */ } // → post-claim install nudge
      try { await supabase.auth.refreshSession(); } catch { /* non-fatal */ }
    }
    onRefetch();
    await new Promise((r) => setTimeout(r, 1000));
    setChecking(false);
    setCheckMsg(
      confirmedAny ? null
        : failed ? tr('creator.claim.checkFailed', 'Не смогли проверить — это сбой на нашей стороне, не ваш. Попробуйте через минуту; мы также проверим автоматически.')
        : throttled ? tr('creator.claim.throttled', 'Проверка уже идёт — попробуйте через {{s}} сек.', { s: throttled })
        : tr('creator.claim.stillPending', 'Пока не видим ваш комментарий. Мы проверяем автоматически в течение трёх дней — экран обновится сам.'),
    );
  };

  // While a claim is open: re-read its status from the DB every 30s (free — the background
  // verifier or an admin may confirm it), and when the user returns to this tab from
  // Instagram, run the check for them instead of waiting for a tap (at most once a minute).
  useEffect(() => {
    if (!claim?.id) return;
    const poll = setInterval(async () => {
      if (await alreadyResolved()) {
        try { sessionStorage.setItem('adamar_just_claimed', '1'); } catch { /* ignore */ }
        try { await supabase.auth.refreshSession(); } catch { /* non-fatal */ }
        onRefetch();
      }
    }, 30_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !checking && Date.now() - lastCheckAt.current > 60_000) void checkStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claim?.id, checking]);

  // ---- Pending state: show the code + how to verify ----
  if (claim) {
    return (
      <div id="verify" style={embedded ? embeddedWrap : wrap}>
        <style>{`.cbtn{transition:transform .08s ease,filter .15s ease}.cbtn:hover{filter:brightness(1.1)}.cbtn:active{transform:scale(.97);filter:brightness(.88)}`}</style>
        <h2 style={embedded ? { ...h2, fontSize: 17 } : h2}>{tr('creator.claim.pendingTitle', 'Подтвердите аккаунт')}</h2>
        <p style={sub}>{tr('creator.claim.pendingFor', 'Аккаунт')}: <b style={{ color: 'var(--accent-text)' }}>@{claim.handle}</b></p>
        {embedded && (
          <p style={{ ...sub, marginTop: 6 }}>
            {tr('creator.claim.whyVerify', 'Статистика уже открыта. Подтверждение нужно, чтобы откликаться на задания, указать цены и контакты и предлагать коллабы — один комментарий в Instagram.')}
          </p>
        )}

        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ color: 'var(--dim)', fontSize: 12, marginBottom: 6 }}>{tr('creator.claim.yourCode', 'Ваш код')}</div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: 1, color: '#fff' }}>{claim.claim_code}</div>
          <button type="button" className="cbtn"
            style={{ ...ghost, ...(copied ? { color: '#5fd17a', borderColor: 'rgba(95,209,122,0.55)' } : {}) }}
            onClick={() => copy(claim.claim_code)}>
            {copied ? tr('creator.claim.copied', 'Скопировано ✓') : tr('creator.claim.copy', 'Скопировать код')}
          </button>
        </div>

        <ol style={steps}>
          <li>{tr('creator.claim.step1', 'Откройте наш официальный пост в Instagram.')}</li>
          <li>{tr('creator.claim.step2', 'Оставьте комментарий с этим кодом с вашего аккаунта @{{h}}.', { h: claim.handle })}</li>
          <li>{tr('creator.claim.step3', 'Вернитесь сюда — мы проверим сами. Или нажмите «Проверить статус».')}</li>
        </ol>

        {CREATOR_VERIFY_POST_URL ? (
          <a href={CREATOR_VERIFY_POST_URL} target="_blank" rel="noreferrer" className="cbtn" style={{ ...primary, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            {tr('creator.claim.openPost', 'Открыть пост Adamar')}
          </a>
        ) : (
          <div style={note}>{tr('creator.claim.postSoon', 'Ссылка на пост появится скоро. Наша команда подтвердит ваш аккаунт вручную в ближайшее время.')} (@{CREATOR_OFFICIAL_HANDLE})</div>
        )}

        <button type="button" className="cbtn" disabled={checking}
          style={{ ...ghost, marginTop: 12, width: '100%', opacity: checking ? 0.6 : 1 }} onClick={checkStatus}>
          {checking ? tr('creator.claim.checking', 'Проверяем…') : tr('creator.claim.check', 'Проверить статус')}
        </button>
        {checkMsg && <div style={{ ...note, marginTop: 10 }}>{checkMsg}</div>}
        {err && <div style={{ color: '#e8607a', fontSize: 13, marginTop: 8 }}>{err}</div>}
        <button type="button" disabled={busy || checking} onClick={changeHandle}
          style={{ background: 'none', border: 0, padding: 0, marginTop: 12, color: '#8ab4ff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' }}>
          {tr('creator.claim.changeHandle', 'Не тот аккаунт? Изменить @никнейм')}
        </button>
        {!embedded && <p style={{ ...sub, marginTop: 10 }}>{tr('creator.claim.pendingHint', 'Как только мы увидим ваш комментарий, откроется ваш кабинет: статистика, медиакит и задания от брендов.')}</p>}
        {!embedded && switchRow}
      </div>
    );
  }

  // ---- Start state: enter handle ----
  return (
    <div style={wrap}>
      <style>{`.cbtn{transition:transform .08s ease,filter .15s ease}.cbtn:hover{filter:brightness(1.1)}.cbtn:active{transform:scale(.97);filter:brightness(.88)}`}</style>
      <h2 style={h2}>{tr('creator.claim.title', 'Это ваш аккаунт?')}</h2>
      <p style={sub}>{tr('creator.claim.introInstant', 'Впишите свой Instagram — кабинет со статистикой откроется сразу.')}</p>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input value={handle} onChange={(e) => setHandle(e.target.value.replace(/\s/g, ''))}
          placeholder={tr('creator.claim.handlePh', '@ваш_никнейм')} style={input} autoCapitalize="none" autoCorrect="off" />
      </div>
      {err && <div style={{ color: '#e8607a', fontSize: 13, marginTop: 8 }}>{err}</div>}
      <button type="button" className="cbtn" disabled={busy || handle.trim().length < 2} onClick={() => start()}
        style={{ ...primary, marginTop: 12, width: '100%', opacity: busy || handle.trim().length < 2 ? 0.6 : 1 }}>
        {busy ? '…' : tr('creator.claim.continue', 'Продолжить')}
      </button>
      {switchRow}
    </div>
  );
}

const wrap: React.CSSProperties = { maxWidth: 480, margin: '0 auto', padding: '24px 16px' };
const embeddedWrap: React.CSSProperties = { background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.3)', borderRadius: 12, padding: 16, margin: '8px 0 16px', scrollMarginTop: 70 };
const h2: React.CSSProperties = { color: '#dde', margin: '0 0 6px', fontSize: 20 };
const sub: React.CSSProperties = { color: '#99a', fontSize: 13, margin: 0, lineHeight: 1.5 };
const card: React.CSSProperties = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, margin: '16px 0' };
const steps: React.CSSProperties = { color: '#aab', fontSize: 13.5, lineHeight: 1.7, paddingLeft: 18, margin: '4px 0 16px' };
const note: React.CSSProperties = { background: 'rgba(230,200,74,0.08)', border: '1px solid rgba(230,200,74,0.25)', color: '#d9c86a', fontSize: 12.5, borderRadius: 10, padding: '10px 12px' };
const input: React.CSSProperties = { flex: 1, padding: '11px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.25)', color: '#eee', border: '1px solid rgba(74,158,255,0.3)', fontSize: 16, fontFamily: 'inherit' };
const primary: React.CSSProperties = { padding: '12px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' };
const ghost: React.CSSProperties = { marginTop: 10, padding: '9px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: '#cdd', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
